import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ticketsApi } from '../lib/api-hooks';
import { useAuth } from '../store/auth';
import { useToast } from '../components/ui/Toast';
import type { Ticket, TicketStatus, Prioritet } from '../lib/types';
import { clsx } from 'clsx';

const COLUMNS: { status: TicketStatus; label: string; color: string; dot: string }[] = [
  { status: 'NY', label: 'Ny', color: 'border-t-blue-500', dot: '#3b4fd6' },
  { status: 'UNDER_ARBEID', label: 'Under arbeid', color: 'border-t-amber-500', dot: '#dd9a1f' },
  { status: 'VENTER_KUNDE', label: 'Venter kunde', color: 'border-t-purple-500', dot: '#8b5cf6' },
  { status: 'VENTER_INTERNT', label: 'Venter internt', color: 'border-t-orange-500', dot: '#f97316' },
  { status: 'LOST', label: 'Løst', color: 'border-t-green-500', dot: '#22946a' },
  { status: 'LUKKET', label: 'Lukket', color: 'border-t-slate-400', dot: '#9ca0aa' },
];

const PRIORITY_COLORS: Record<Prioritet, string> = {
  KRITISK: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  HOY: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  NORMAL: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  LAV: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
};

const PRIORITY_LABELS: Record<Prioritet, string> = {
  KRITISK: 'Kritisk',
  HOY: 'Høy',
  NORMAL: 'Normal',
  LAV: 'Lav',
};

function formatSakNr(n: number): string {
  return `SAK-${String(n).padStart(4, '0')}`;
}

function getInitials(navn?: string | null): string {
  if (!navn) return '?';
  return navn.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

type TicketRow = Ticket & {
  customer?: { id: string; navn: string | null; epost: string } | null;
  assignee?: { id: string; navn: string; avatarUrl?: string | null } | null;
  _count?: { messages: number };
};

export default function KanbanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<'all' | 'mine' | 'unassigned'>('all');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<TicketStatus | null>(null);
  const dragCounter = useRef(0);

  const { data, isLoading } = useQuery({
    queryKey: ['kanban-tickets', filter],
    queryFn: () => ticketsApi.list({ filter: filter === 'all' ? undefined : filter }),
    refetchInterval: 30_000,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TicketStatus }) =>
      ticketsApi.update(id, { status }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['kanban-tickets', filter] });
      const prev = qc.getQueryData<{ tickets: TicketRow[] }>(['kanban-tickets', filter]);
      qc.setQueryData<{ tickets: TicketRow[] }>(['kanban-tickets', filter], (old) => {
        if (!old) return old;
        return {
          ...old,
          tickets: old.tickets.map((t) => (t.id === id ? { ...t, status } : t)),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['kanban-tickets', filter], ctx.prev);
      toast.error('Kunne ikke endre status.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['kanban-tickets'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const tickets: TicketRow[] = data?.tickets ?? [];

  const filtered = useMemo(() => {
    if (filter === 'mine') return tickets.filter((t) => t.assigneeId === user?.id);
    if (filter === 'unassigned') return tickets.filter((t) => !t.assigneeId);
    return tickets;
  }, [tickets, filter, user?.id]);

  const byStatus = useMemo(() => {
    const map: Record<TicketStatus, TicketRow[]> = {
      NY: [], UNDER_ARBEID: [], VENTER_KUNDE: [], VENTER_INTERNT: [], LOST: [], LUKKET: [],
    };
    for (const t of filtered) {
      if (map[t.status]) map[t.status].push(t);
    }
    // Sorter etter prioritet (KRITISK først), deretter sakNr
    const prioOrder: Record<Prioritet, number> = { KRITISK: 0, HOY: 1, NORMAL: 2, LAV: 3 };
    for (const k of Object.keys(map) as TicketStatus[]) {
      map[k].sort((a, b) => {
        const pd = prioOrder[a.prioritet] - prioOrder[b.prioritet];
        if (pd !== 0) return pd;
        return a.sakNr - b.sakNr;
      });
    }
    return map;
  }, [filtered]);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverCol(null);
    dragCounter.current = 0;
  };

  const handleDragOver = (e: React.DragEvent, _status: TicketStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (status: TicketStatus) => {
    dragCounter.current++;
    setDragOverCol(status);
  };

  const handleDragLeave = () => {
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setDragOverCol(null);
      dragCounter.current = 0;
    }
  };

  const handleDrop = (e: React.DragEvent, status: TicketStatus) => {
    e.preventDefault();
    const id = draggedId ?? e.dataTransfer.getData('text/plain');
    if (id) {
      const ticket = tickets.find((t) => t.id === id);
      if (ticket && ticket.status !== status) {
        updateStatus.mutate({ id, status });
        toast.success(`Flyttet til ${COLUMNS.find((c) => c.status === status)?.label}`);
      }
    }
    handleDragEnd();
  };

  return (
    <div className="flex h-[calc(100vh-49px)] flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900 md:px-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Kanban</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Dra saker mellom kolonner for å endre status
            </p>
          </div>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
            {([
              { key: 'all', label: 'Alle' },
              { key: 'mine', label: 'Mine' },
              { key: 'unassigned', label: 'Ufordelte' },
            ] as const).map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                  filter === f.key
                    ? 'bg-white text-slate-800 shadow-sm dark:bg-slate-700 dark:text-slate-100'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Board */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <span className="animate-pulse text-slate-400">Laster saker…</span>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex h-full min-w-max gap-3 p-4 md:p-6">
            {COLUMNS.map((col) => {
              const colTickets = byStatus[col.status];
              return (
                <div
                  key={col.status}
                  className={clsx(
                    'flex w-72 shrink-0 flex-col rounded-xl border-t-4 bg-slate-100/60 transition-colors dark:bg-slate-900/40',
                    col.color,
                    dragOverCol === col.status && 'ring-2 ring-brand-400 ring-inset bg-brand-50/50 dark:bg-brand-500/5',
                  )}
                  onDragOver={(e) => handleDragOver(e, col.status)}
                  onDragEnter={() => handleDragEnter(col.status)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, col.status)}
                >
                  {/* Column header */}
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: col.dot }}
                      />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        {col.label}
                      </span>
                    </div>
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      {colTickets.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-3">
                    {colTickets.length === 0 ? (
                      <div className="flex h-20 items-center justify-center text-xs text-slate-400">
                        Tomt
                      </div>
                    ) : (
                      colTickets.map((t) => (
                        <KanbanCard
                          key={t.id}
                          ticket={t}
                          isDragging={draggedId === t.id}
                          onClick={() => navigate(`/ticket/${t.id}`)}
                          onDragStart={(e) => handleDragStart(e, t.id)}
                          onDragEnd={handleDragEnd}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function KanbanCard({
  ticket,
  isDragging,
  onClick,
  onDragStart,
  onDragEnd,
}: {
  ticket: TicketRow;
  isDragging: boolean;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={clsx(
        'cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-all hover:border-brand-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 dark:hover:border-brand-500/50',
        isDragging && 'opacity-40 rotate-2',
      )}
    >
      {/* Saknr + prioritet */}
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-mono text-xs text-slate-400">{formatSakNr(ticket.sakNr)}</span>
        <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-semibold', PRIORITY_COLORS[ticket.prioritet])}>
          {PRIORITY_LABELS[ticket.prioritet]}
        </span>
      </div>

      {/* Tittel */}
      <p className="mb-2 line-clamp-2 text-sm font-medium text-slate-800 dark:text-slate-100">
        {ticket.tittel}
      </p>

      {/* Footer: kunde + assignee */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          {ticket.customer ? (
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {ticket.customer.navn || ticket.customer.epost}
            </p>
          ) : (
            <p className="text-xs text-slate-300 dark:text-slate-600">Ingen kunde</p>
          )}
        </div>
        {ticket.assignee ? (
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-brand text-[10px] font-semibold text-white"
            title={ticket.assignee.navn}
          >
            {ticket.assignee.avatarUrl ? (
              <img src={ticket.assignee.avatarUrl} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              getInitials(ticket.assignee.navn)
            )}
          </div>
        ) : (
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-300 text-[10px] text-slate-400 dark:border-slate-600" title="Ufordelt">
            ?
          </div>
        )}
      </div>

      {/* Kategori + meldinger */}
      {(ticket.kategori || ticket._count?.messages) && (
        <div className="mt-2 flex items-center gap-2 border-t border-slate-100 pt-2 dark:border-slate-700/50">
          {ticket.kategori && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 dark:bg-slate-700 dark:text-slate-300">
              {ticket.kategori}
            </span>
          )}
          {ticket._count?.messages ? (
            <span className="flex items-center gap-1 text-[10px] text-slate-400">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              {ticket._count.messages}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
